import { useMemo, useState } from "react";
import {
  CorrectionFormContext,
  type CorrectionFormContextType,
} from "./context";
import { parseAsString, useQueryState } from "nuqs";
import type {
  FormalityRequestType,
  FormDetailResponse,
  FORM_ASSIGNMENTS_STATUS,
} from "@/types/entities/formality-request";
import type { Correction, Revision } from "@/queries/formality-request";
import type { UserProfile } from "@/queries/me";

export type FormAssignmentStatusType = keyof typeof FORM_ASSIGNMENTS_STATUS;

export const CorrectionFormProvider = ({
  children,
  formalityRequest,
  form,
  userProfile,
  revisionsRaw = [],
}: {
  children: React.ReactNode;
  formalityRequest: FormalityRequestType | undefined;
  form: FormDetailResponse | undefined;
  revisionsRaw: Revision[] | undefined;
  userProfile: UserProfile | undefined;
}) => {
  const [rejectComments, setRejectComments] = useState<string>("");
  const [selectedRevisionId, setRevisionId] = useQueryState(
    "revision_id",
    parseAsString.withDefault(""),
  );
  const [selectedGroupId, setGroupId] = useQueryState(
    "group_id",
    parseAsString,
  );

  const [needsCorrectionCount, setNeedsCorrectionCount] = useState(0);

  const [corrections, setCorrections] = useState<
    Map<string, { status: "DENIED" | "APPROVED"; reason?: string }>
  >(new Map());

  // Set de IDs de campos que fueron aprobados en ciclos anteriores y no pueden ser denegados
  const [lockedApprovedFields, setLockedApprovedFields] = useState<Set<string>>(
    new Set(),
  );
  const [nonPreventableFields, setNonPreventableFields] = useState<Set<string>>(
    new Set(),
  );

  const revisionId = useMemo(() => {
    // Only use revision if explicitly selected from URL
    if (selectedRevisionId) return selectedRevisionId;

    // Don't auto-select any revision - let user choose
    return "";
  }, [selectedRevisionId]);

  // Extract user group IDs
  const userGroupIds = useMemo(() => {
    return new Set(userProfile?.data?.groups?.map((group) => group.id) || []);
  }, [userProfile]);

  // Filter revisions to show only groups that the user belongs to
  const revisions = useMemo(() => {
    return revisionsRaw.map((revision) => {
      const filteredGroups =
        revision.groups?.filter((group) => userGroupIds.has(group.id)) || [];
      const filteredCorrections =
        revision.corrections?.filter((correction) => {
          const groupId = correction.group?.id;
          return groupId && userGroupIds.has(groupId);
        }) || [];
      return {
        ...revision,
        groups: filteredGroups,
        corrections: filteredCorrections,
      };
    });
  }, [revisionsRaw, userGroupIds]);

  // Verificar si el grupo seleccionado ya envió su corrección en el ciclo actual
  const groupCorrectionInCurrentCycle = useMemo(() => {
    if (!selectedRevisionId || !form) {
      return null;
    }

    // Usar form.form_assignments[0].revisions porque tiene current_cycle
    const revisionFromData = form?.form_assignments?.[0]?.revisions?.find(
      (r) => r.id === selectedRevisionId,
    );

    if (!revisionFromData) {
      return null;
    }

    const totalGroups =
      (revisionFromData as { groups?: Array<{ id: string; name: string }> })
        .groups?.length || 0;

    const corrections =
      (
        revisionFromData as {
          corrections?: Array<{
            id: string;
            group?: { id: string };
            group_id?: string;
            status: "APPROVED" | "DENIED" | "IN_PROCESS";
            comments?: Array<{ text: string }>;
          }>;
        }
      ).corrections || [];

    // Si hay grupos, buscar la corrección del grupo seleccionado
    if (totalGroups > 0 && selectedGroupId) {
      // Calcular el rango de correcciones para el ciclo actual
      // const startIdx = (currentCycle - 1) * totalGroups;
      // const endIdx = currentCycle * totalGroups;
      // console.log(startIdx, endIdx);
      // const currentCycleCorrections = corrections.slice(startIdx, endIdx);
      // console.log(currentCycleCorrections);
      // Buscar la corrección del grupo seleccionado
      const groupCorrection = corrections.find((correction) => {
        const groupId = correction?.group?.id || correction?.group_id;
        return (
          groupId === selectedGroupId && correction.status === "IN_PROCESS"
        );
      });

      // Enriquecer con datos completos de la revisión del API (que tiene creator)
      if (groupCorrection) {
        const revisionFromAPI = revisions.find(
          (r) => r.id === selectedRevisionId,
        );
        const fullCorrection = revisionFromAPI?.corrections?.find(
          (c) => c.id === groupCorrection.id,
        );

        return (fullCorrection || groupCorrection) as Correction;
      }

      return groupCorrection ?? null;
    }
    // Si no hay grupos (o solo hay un grupo implícito), verificar si hay correcciones para esta revisión
    // Esto maneja el caso cuando hay correcciones pero no hay múltiples grupos
    if (corrections.length > 0) {
      // Si hay correcciones para esta revisión, retornar la más reciente
      // Esto hace que se muestre la vista de solo lectura cuando ya se enviaron observaciones
      const lastCorrection = corrections[0];

      // Enriquecer con datos completos de la revisión del API (que tiene creator)
      if (lastCorrection) {
        const revisionFromAPI = revisions.find(
          (r) => r.id === selectedRevisionId,
        );
        const fullCorrection = revisionFromAPI?.corrections?.find(
          (c) => c.id === lastCorrection.id,
        );
        return (fullCorrection || lastCorrection) as Correction;
      }

      return lastCorrection || null;
    }

    return null;
  }, [selectedRevisionId, selectedGroupId, form, revisions]);

  // Determinar si se debe mostrar la vista de solo lectura
  const isReadOnlyView = groupCorrectionInCurrentCycle !== null;

  // Determinar si la revisión seleccionada es la última (más reciente)
  const isLatestRevision = useMemo(() => {
    if (!selectedRevisionId || revisions.length === 0) return true;

    // Ordenar revisiones por fecha de creación
    const sortedRevisions = [...revisions].sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return dateB - dateA; // Más reciente primero
    });

    // Verificar si la revisión seleccionada es la primera (más reciente)
    return sortedRevisions[0]?.id === selectedRevisionId;
  }, [selectedRevisionId, revisions]);

  // Obtener la revisión seleccionada para verificar requires_leader_approval
  const selectedRevision = useMemo(() => {
    if (!selectedRevisionId) return null;
    return revisions.find((rev) => rev.id === selectedRevisionId) || null;
  }, [selectedRevisionId, revisions]);

  const groupsCount = useMemo(() => {
    if (!selectedRevision) return 0;
    return selectedRevision.groups?.length || 0;
  }, [selectedRevision]);

  // Verificar si el usuario es líder del grupo seleccionado
  const isUserLeaderOfSelectedGroup = useMemo(() => {
    if (!selectedGroupId || !userProfile?.data?.groups) return false;
    const group = userProfile.data.groups.find((g) => g.id === selectedGroupId);
    return group?.pivot?.seniority === 1 || group?.pivot?.seniority === 2;
  }, [selectedGroupId, userProfile]);

  const contextValue = useMemo<CorrectionFormContextType>(() => {
    return {
      rejectComments,
      setRejectComments,
      selectedRevisionId,
      setRevisionId,
      selectedGroupId,
      setGroupId,
      revisionId,
      groupCorrectionInCurrentCycle,
      isReadOnlyView,
      revisions,
      userProfile,
      form,
      isUserLeaderOfSelectedGroup,
      groupsCount,
      isLatestRevision,
      selectedRevision,
      formalityRequest,
      corrections,
      setCorrections,
      lockedApprovedFields,
      setLockedApprovedFields,
      nonPreventableFields,
      setNonPreventableFields,
      needsCorrectionCount,
      setNeedsCorrectionCount,
    };
  }, [
    rejectComments,
    setRejectComments,
    selectedRevisionId,
    setRevisionId,
    selectedGroupId,
    setGroupId,
    revisionId,
    groupCorrectionInCurrentCycle,
    isReadOnlyView,
    revisions,
    userProfile,
    form,
    isUserLeaderOfSelectedGroup,
    groupsCount,
    isLatestRevision,
    selectedRevision,
    formalityRequest,
    corrections,
    setCorrections,
    lockedApprovedFields,
    setLockedApprovedFields,
    nonPreventableFields,
    setNonPreventableFields,
    needsCorrectionCount,
    setNeedsCorrectionCount,
  ]);
  return (
    <CorrectionFormContext.Provider value={contextValue}>
      {children}
    </CorrectionFormContext.Provider>
  );
};
